/**
 * SkipLink - Skip to main content link for keyboard users
 * 
 * Should be the first focusable element on the page.
 * 
 * @example
 * // In App.tsx layout
 * <SkipLink targetId="main-content" />
 * <Header />
 * <main id="main-content">...</main>
 */

export interface SkipLinkProps {
  /** ID of the target element */
  targetId?: string;
  /** Link text */
  children?: string;
  /** Additional CSS classes */
  className?: string;
}

export function SkipLink({
  targetId = "main-content",
  children = "Skip to main content",
  className = "",
}: SkipLinkProps) {
  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const target = document.getElementById(targetId);
    if (target) {
      target.tabIndex = -1;
      target.focus();
      target.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <a
      href={`#${targetId}`}
      onClick={handleClick}
      className={`
        fixed left-4 top-4 z-[9999]
        -translate-y-full opacity-0
        rounded-lg bg-gradient-to-r from-[#00B894] to-[#0984E3]
        px-4 py-2 text-sm font-medium text-white
        shadow-lg transition-all duration-200
        focus:translate-y-0 focus:opacity-100
        focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2
        ${className}
      `}
    >
      {children}
    </a>
  );
}

export default SkipLink;
